import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { updateProfile, updateApiKey, updatePassword } from '../api';

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('personal');
  
  // Profile Loading
  const [loading, setLoading] = useState(false);
  // Password Loading
  const [passLoading, setPassLoading] = useState(false);

  // --- Personal Info State ---
  const [personalInfo, setPersonalInfo] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    gst: '',
    country: 'India',
    address: ''
  });

  useEffect(() => {
    if (user) {
      setPersonalInfo({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        gst: user.gst || '',
        country: user.country || 'India',
        address: user.address || ''
      });
    }
  }, [user]);

  // --- Password State & Working Eye Toggles ---
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [showPass, setShowPass] = useState({ current: false, new: false, confirm: false });

  // --- API Key State ---
  const [apiKey, setApiKey] = useState(user?.api_token || 'No API key generated yet');

  const togglePasswordVisibility = (field) => {
    setShowPass(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiKey);
    alert('API Key copied to clipboard!');
  };

  // --- Handle Generate API Key ---
  const handleGenerateKey = async () => {
    try {
      const response = await updateApiKey();
      if (response.status === 1) {
        setApiKey(response.data.api_token);
        alert('✅ New API Key generated successfully!');
        if (refreshUser) await refreshUser();
      }
    } catch (error) {
      alert(error.description || 'Failed to generate API Key.');
    }
  };

  // --- Handle Profile Info Submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await updateProfile(personalInfo);
      if (response.status === 1) {
        alert('✅ Profile updated successfully!');
        if (refreshUser) await refreshUser();
      }
    } catch (error) {
      alert(error.description || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  // --- ✅ Handle Change Password Submit ---
  const handlePasswordSubmit = async () => {
    // Validation
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      return alert('Please fill in all password fields.');
    }
    if (passwords.new !== passwords.confirm) {
      return alert('New password and confirm password do not match.');
    }

    setPassLoading(true);
    try {
      const response = await updatePassword({
        currentPassword: passwords.current,
        newPassword: passwords.new
      });
      if (response.status === 1) {
        alert('✅ Password updated successfully!');
        setPasswords({ current: '', new: '', confirm: '' }); // Clear fields on success
      }
    } catch (error) {
      alert(error.description || 'Failed to update password.');
    } finally {
      setPassLoading(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col p-4 md:p-6">
      <div className="w-full bg-white rounded-xl shadow-md border border-gray-100 p-6 md:p-8">
        
        {/* Header and Tabs */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-gray-200 pb-4 mb-6">
          <h1 className="text-2xl font-semibold text-gray-800 mb-4 md:mb-0">Profile Account</h1>
          <div className="flex gap-6 text-sm font-medium">
            <button 
              className={`pb-2 border-b-2 transition-colors ${activeTab === 'personal' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
              onClick={() => setActiveTab('personal')}
            >
              Personal Info
            </button>
            <button 
              className={`pb-2 border-b-2 transition-colors ${activeTab === 'password' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
              onClick={() => setActiveTab('password')}
            >
              Change Password
            </button>
            <button 
              className={`pb-2 border-b-2 transition-colors ${activeTab === 'apikey' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
              onClick={() => setActiveTab('apikey')}
            >
              API Key
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          
          {/* 1. Personal Info Tab */}
          {activeTab === 'personal' && (
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Name: *</label>
                <input required type="text" value={personalInfo.name} onChange={(e) => setPersonalInfo({...personalInfo, name: e.target.value})} className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-600 focus:border-green-600 block w-full p-2.5 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email: *</label>
                <input required type="email" value={personalInfo.email} onChange={(e) => setPersonalInfo({...personalInfo, email: e.target.value})} className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-600 focus:border-green-600 block w-full p-2.5 outline-none transition-all" />
              </div>
              
              {/* Phone Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone: *</label>
                <input required type="text" value={personalInfo.phone} onChange={(e) => setPersonalInfo({...personalInfo, phone: e.target.value})} className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-600 focus:border-green-600 block w-full p-2.5 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Name:</label>
                <input type="text" value={personalInfo.company} onChange={(e) => setPersonalInfo({...personalInfo, company: e.target.value})} className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-600 focus:border-green-600 block w-full p-2.5 outline-none transition-all" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">GST No:</label>
                <input type="text" placeholder="Gst No." value={personalInfo.gst} onChange={(e) => setPersonalInfo({...personalInfo, gst: e.target.value})} className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-600 focus:border-green-600 block w-full p-2.5 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Country:</label>
                <select value={personalInfo.country} onChange={(e) => setPersonalInfo({...personalInfo, country: e.target.value})} className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-600 focus:border-green-600 block w-full p-2.5 outline-none transition-all">
                  <option>India</option>
                  <option>USA</option>
                  <option>UK</option>
                  <option>Australia</option>
                </select>
              </div>
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Address:</label>
                <textarea rows={3} value={personalInfo.address} onChange={(e) => setPersonalInfo({...personalInfo, address: e.target.value})} className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-600 focus:border-green-600 block w-full p-2.5 outline-none transition-all resize-none" />
              </div>

              <div className="col-span-1 md:col-span-2 flex justify-end mt-4">
                <button disabled={loading} type="submit" className="bg-green-700 hover:bg-green-800 text-white font-medium py-2.5 px-10 rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? 'Saving...' : 'Submit'}
                </button>
              </div>
            </form>
          )}

          {/* 2. Change Password Tab (WITH WORKING LOGIC) */}
          {activeTab === 'password' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Current Password: *</label>
                <div className="relative">
                  <input type={showPass.current ? 'text' : 'password'} placeholder="............" value={passwords.current} onChange={(e) => setPasswords({...passwords, current: e.target.value})} className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-600 focus:border-green-600 block w-full p-2.5 outline-none transition-all pr-10" />
                  <button type="button" onClick={() => togglePasswordVisibility('current')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer z-10">
                    {showPass.current ? <EyeIconOn /> : <EyeIconOff />} 
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password: *</label>
                <div className="relative">
                  <input type={showPass.new ? 'text' : 'password'} placeholder="............" value={passwords.new} onChange={(e) => setPasswords({...passwords, new: e.target.value})} className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-600 focus:border-green-600 block w-full p-2.5 outline-none transition-all pr-10" />
                  <button type="button" onClick={() => togglePasswordVisibility('new')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer z-10">
                    {showPass.new ? <EyeIconOn /> : <EyeIconOff />}
                  </button>
                </div>
              </div>
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password: *</label>
                <div className="relative md:w-[calc(50%-12px)]">
                  <input type={showPass.confirm ? 'text' : 'password'} placeholder="............" value={passwords.confirm} onChange={(e) => setPasswords({...passwords, confirm: e.target.value})} className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-600 focus:border-green-600 block w-full p-2.5 outline-none transition-all pr-10" />
                  <button type="button" onClick={() => togglePasswordVisibility('confirm')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer z-10">
                    {showPass.confirm ? <EyeIconOn /> : <EyeIconOff />}
                  </button>
                </div>
              </div>
              <div className="col-span-1 md:col-span-2 flex justify-end mt-4">
                {/* ✅ NOW HAS AN onClick HANDLER AND DISABLED STATE */}
                <button 
                  onClick={handlePasswordSubmit} 
                  disabled={passLoading} 
                  className="bg-green-700 hover:bg-green-800 text-white font-medium py-2.5 px-10 rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passLoading ? 'Updating...' : 'Submit'}
                </button>
              </div>
            </div>
          )}

          {/* 3. API Key Tab */}
          {activeTab === 'apikey' && (
            <div className="flex flex-col gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Web API Key</label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input readOnly value={apiKey} className="flex-1 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg px-4 py-2.5 outline-none" />
                  <div className="flex gap-3">
                    <button onClick={handleGenerateKey} className="bg-green-700 hover:bg-green-800 text-white font-medium py-2.5 px-6 rounded-lg transition-colors shadow-sm whitespace-nowrap">Generate</button>
                    <button onClick={copyToClipboard} className="bg-green-700 hover:bg-green-800 text-white font-medium py-2.5 px-6 rounded-lg transition-colors shadow-sm whitespace-nowrap">Copy API</button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// --- Eye Icon Components ---
const EyeIconOn = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeIconOff = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);